import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Header from "./Header";
import Footer from "./Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DUMMY_PROJECTS = [
  { id: "1", title: "NeuroSync Analytics Dashboard", category: "AI & Analytics", description: "A real-time AI-powered analytics platform that processes millions of data points to surface actionable business intelligence for enterprise clients." },
  { id: "2", title: "Verdant Supply Chain OS", category: "Full-Stack", description: "End-to-end supply chain management platform with predictive restocking, supplier risk scoring, and live logistics tracking across 14 countries." },
  { id: "3", title: "PulseHealth AI", category: "AI & Healthcare", description: "Clinical decision support system using transformer-based models to assist clinicians in early detection of metabolic disorders from routine lab results." },
  { id: "4", title: "UrbanFlow City Platform", category: "Full-Stack", description: "Smart city operations platform unifying traffic, utilities, and municipal services into a single dashboard used by 3 metropolitan councils." },
  { id: "5", title: "ClearVoice NLP Engine", category: "AI & Analytics", description: "Multilingual sentiment and intent engine processing 2M+ customer interactions daily, integrated into enterprise CRM and ticketing workflows." },
  { id: "6", title: "EchoCommerce Intelligence", category: "Full-Stack", description: "Personalisation and demand forecasting layer for a mid-market e-commerce group, reducing overstock by 38% in the first quarter post-launch." },
];
const PAGE_GRADIENT = "linear-gradient(180deg, #0C1A19 0%, #080C0C 20%, #080C0C 80%, #040808 100%)";

const ProjectCard = ({ project }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link to={`/project/${project.id}`} style={{ textDecoration: "none" }}>
      <div
        className="portfolio-card"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: "linear-gradient(145deg, #141f1e 0%, #0c1717 100%)",
          border: "1px solid",
          borderColor: hovered ? "rgba(91,148,144,0.45)" : "rgba(91,148,144,0.16)",
          borderRadius: 12,
          overflow: "hidden",
          transform: hovered ? "translateY(-3px)" : "translateY(0)",
          transition: "all 0.22s ease",
          display: "flex",
          flexDirection: "column",
          boxShadow: hovered
            ? "0 0 32px rgba(91,148,144,0.20), 0 8px 28px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)"
            : "0 0 18px rgba(91,148,144,0.08), 0 4px 16px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        {/* Image — aspect-ratio locked via padding-top trick */}
        <div className="portfolio-card-img" style={{ position: "relative", paddingTop: "56.25%", background: "linear-gradient(135deg, #1a2e2e 0%, #0a1414 100%)", flexShrink: 0, overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0 }}>
            {project.cardImageType === "logo" && project.logoImage ? (
              <>
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #1a2e2e, #0f1a1a)" }} />
                <img
                  src={project.logoImage}
                  alt={project.title}
                  style={{
                    position: "absolute",
                    top: "50%", left: "50%",
                    transform: "translate(-50%, -50%)",
                    maxWidth: "60%", maxHeight: "70%",
                    objectFit: "contain"
                  }}
                />
              </>
            ) : project.bannerImage ? (
              <img src={project.bannerImage} alt={project.title} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #1a2e2e, #0f1a1a)" }} />
            )}
          </div>
          {project.category && (
            <span
              className="portfolio-card-tag"
              style={{
                position: "absolute",
                bottom: "12px",
                left: "14px",
                background: "linear-gradient(135deg, rgba(91,148,144,0.28) 0%, rgba(45,92,90,0.18) 100%)",
                border: "1px solid rgba(91,148,144,0.85)",
                borderRadius: 9999,
                padding: "4px 12px",
                fontFamily: "Montserrat, sans-serif",
                fontSize: "10px",
                fontWeight: 500,
                color: "#a8dbd8",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                boxShadow: "0 0 10px rgba(91,148,144,0.30), inset 0 1px 0 rgba(255,255,255,0.10)",
                whiteSpace: "nowrap",
              }}>
              {project.category}
            </span>
          )}
        </div>
        <div className="portfolio-card-body" style={{ padding: "clamp(10px,2.5vw,16px) clamp(10px,2.5vw,20px) clamp(12px,2.5vw,20px)", flex: 1, display: "flex", flexDirection: "column" }}>
          <h3 className="portfolio-card-title" style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: "clamp(11px,1.8vw,14px)", color: "#fff", marginBottom: 6 }}>
            {project.title}
          </h3>
          <p className="portfolio-card-desc" style={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 300,
            fontSize: "clamp(10px,1.4vw,12px)",
            color: "rgba(255,255,255,0.3)",
            lineHeight: 1.6,
            margin: 0,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical"
          }}>
            {project.description}
          </p>
        </div>
      </div>
    </Link>
  );
};

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const heroHeadingRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/portfolio?include_all=true`)
      .then(res => { if (res.data.success) setProjects(res.data.data); })
      .catch(() => { setProjects(DUMMY_PROJECTS); })
      .finally(() => setIsLoading(false));
  }, []);

  // Mount effect: hero heading + chips (above fold, no ScrollTrigger)
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      gsap.from(heroHeadingRef.current, {
        clipPath: "inset(0 100% 0 0)",
        duration: 0.9,
        ease: "power3.out",
      });
    });

    return () => ctx.revert();
  }, []);

  // Scroll effect: card grid
  useEffect(() => {
    if (!gridRef.current || gridRef.current.children.length === 0) return;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      gsap.from(gridRef.current.children, {
        y: 24,
        opacity: 0,
        scale: 0.95,
        stagger: 0.07,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: { trigger: gridRef.current, start: "top 85%", once: true },
      });
    });

    return () => ctx.revert();
  }, []);


  return (
    <div style={{ background: PAGE_GRADIENT, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Header />

      {/* Hero */}
      <section style={{
        paddingTop: 128,
        paddingBottom: 48,
        paddingLeft: "clamp(24px, 6vw, 80px)",
        paddingRight: "clamp(24px, 6vw, 80px)"
      }}>
        <h1 ref={heroHeadingRef} style={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 300,
          fontSize: "clamp(36px, 5vw, 64px)",
          color: "#fff",
          letterSpacing: -2,
          marginBottom: 16,
          lineHeight: 1.1
        }}>
          Our <span style={{ color: "#7BB5B1" }}>Work</span>
        </h1>
        <p style={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 300,
          fontSize: 15,
          color: "rgba(255,255,255,0.45)",
          maxWidth: 480,
          lineHeight: 1.7
        }}>
          A showcase of AI solutions and full-stack applications built for forward-thinking teams.
        </p>
      </section>

      {/* Grid — flex:1 pushes Footer to the bottom regardless of card count */}
      <section style={{
        flex: 1,
        paddingLeft: "clamp(24px, 6vw, 80px)",
        paddingRight: "clamp(24px, 6vw, 80px)",
        paddingBottom: 120
      }}>
        {isLoading ? (
          <div className="portfolio-page-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} style={{
                background: "#111",
                border: "1px solid #1e1e1e",
                borderRadius: 12,
                height: 280,
                opacity: 0.4
              }} />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <p style={{
            color: "rgba(255,255,255,0.25)",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 300,
            fontSize: 15
          }}>
            No projects yet.
          </p>
        ) : (
          <div ref={gridRef} className="portfolio-page-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {projects.map(project => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default Portfolio;
